import {
  updateSystemConfigRequestSchema,
  updateSystemSettingsRequestSchema,
  type SystemConfigResponse,
  type SystemSettingsResponse,
  type UpdateSystemConfigRequest,
  type UpdateSystemSettingsRequest,
} from "@multi-chat/shared";
import { formatStorageGigabyteAmount } from "../settings/format-storage-gigabytes.js";
import { parseQuotaGigabytes } from "./organization-entitlement-draft.js";

export interface GlobalSettingsSource {
  config: SystemConfigResponse;
  settings: SystemSettingsResponse;
}

export interface GlobalSettingsDraft {
  contactsUnlimited: boolean;
  contactsValue: string;
  teamUnlimited: boolean;
  teamValue: string;
  storageValue: string;
  storageTouched: boolean;
  whatsappEnabled: boolean;
  facebookEnabled: boolean;
  instagramEnabled: boolean;
  supportEmail: string;
  supportPhone: string;
  supportWhatsApp: string;
  supportUrl: string;
}

export type GlobalSettingsField =
  | "contacts"
  | "team"
  | "storage"
  | "supportEmail"
  | "supportPhone"
  | "supportWhatsApp"
  | "supportUrl";

function parseFiniteCount(value: string, min: number): number | null {
  const trimmed = value.trim();
  if (!/^(?:0|[1-9]\d*)$/.test(trimmed)) {
    return null;
  }
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed < min) {
    return null;
  }
  return parsed;
}

function supportText(value: string | null): string {
  return value ?? "";
}

export function globalSettingsDraftFromSource(source: GlobalSettingsSource): GlobalSettingsDraft {
  return {
    contactsUnlimited: source.config.defaultContactsLimit === null,
    contactsValue:
      source.config.defaultContactsLimit === null ? "" : String(source.config.defaultContactsLimit),
    teamUnlimited: source.config.defaultTeamMembersLimit === null,
    teamValue:
      source.config.defaultTeamMembersLimit === null ? "" : String(source.config.defaultTeamMembersLimit),
    storageValue: formatStorageGigabyteAmount(BigInt(source.config.defaultStorageQuotaBytes)),
    storageTouched: false,
    whatsappEnabled: source.config.defaultWhatsAppEnabled,
    facebookEnabled: source.config.defaultFacebookEnabled,
    instagramEnabled: source.config.defaultInstagramEnabled,
    supportEmail: supportText(source.settings.supportEmail),
    supportPhone: supportText(source.settings.supportPhone),
    supportWhatsApp: supportText(source.settings.supportWhatsApp),
    supportUrl: supportText(source.settings.supportUrl),
  };
}

export function globalSettingsFormKey(source: GlobalSettingsSource): string {
  return [
    source.config.defaultContactsLimit ?? "unlimited",
    source.config.defaultTeamMembersLimit ?? "unlimited",
    source.config.defaultStorageQuotaBytes,
    source.config.defaultWhatsAppEnabled,
    source.config.defaultFacebookEnabled,
    source.config.defaultInstagramEnabled,
    source.settings.supportEmail ?? "",
    source.settings.supportPhone ?? "",
    source.settings.supportWhatsApp ?? "",
    source.settings.supportUrl ?? "",
  ].join(":");
}

function optionalSupport(
  draftValue: string,
  current: string | null,
): string | null | undefined {
  const next = draftValue.trim() === "" ? null : draftValue.trim();
  return next === current ? undefined : next;
}

export function readGlobalSettingsDraft(
  source: GlobalSettingsSource,
  draft: GlobalSettingsDraft,
):
  | { ok: true; config: UpdateSystemConfigRequest; settings: UpdateSystemSettingsRequest }
  | { ok: false; fields: GlobalSettingsField[] } {
  const fields: GlobalSettingsField[] = [];
  const config: UpdateSystemConfigRequest = {};
  const settings: UpdateSystemSettingsRequest = {};

  const contacts = draft.contactsUnlimited ? null : parseFiniteCount(draft.contactsValue, 0);
  if (!draft.contactsUnlimited && contacts === null) {
    fields.push("contacts");
  } else if (contacts !== source.config.defaultContactsLimit) {
    config.defaultContactsLimit = contacts;
  }

  const team = draft.teamUnlimited ? null : parseFiniteCount(draft.teamValue, 1);
  if (!draft.teamUnlimited && team === null) {
    fields.push("team");
  } else if (team !== source.config.defaultTeamMembersLimit) {
    config.defaultTeamMembersLimit = team;
  }

  if (draft.storageTouched) {
    const bytes = parseQuotaGigabytes(draft.storageValue);
    if (bytes === null) {
      fields.push("storage");
    } else if (bytes !== source.config.defaultStorageQuotaBytes) {
      config.defaultStorageQuotaBytes = bytes;
    }
  }

  if (draft.whatsappEnabled !== source.config.defaultWhatsAppEnabled) {
    config.defaultWhatsAppEnabled = draft.whatsappEnabled;
  }
  if (draft.facebookEnabled !== source.config.defaultFacebookEnabled) {
    config.defaultFacebookEnabled = draft.facebookEnabled;
  }
  if (draft.instagramEnabled !== source.config.defaultInstagramEnabled) {
    config.defaultInstagramEnabled = draft.instagramEnabled;
  }

  const supportEmail = optionalSupport(draft.supportEmail, source.settings.supportEmail);
  const supportPhone = optionalSupport(draft.supportPhone, source.settings.supportPhone);
  const supportWhatsApp = optionalSupport(draft.supportWhatsApp, source.settings.supportWhatsApp);
  const supportUrl = optionalSupport(draft.supportUrl, source.settings.supportUrl);
  if (supportEmail !== undefined) settings.supportEmail = supportEmail;
  if (supportPhone !== undefined) settings.supportPhone = supportPhone;
  if (supportWhatsApp !== undefined) settings.supportWhatsApp = supportWhatsApp;
  if (supportUrl !== undefined) settings.supportUrl = supportUrl;

  const configParsed = updateSystemConfigRequestSchema.safeParse(config);
  const settingsParsed = updateSystemSettingsRequestSchema.safeParse(settings);
  if (!configParsed.success) {
    if (config.defaultContactsLimit !== undefined) fields.push("contacts");
    if (config.defaultTeamMembersLimit !== undefined) fields.push("team");
    if (config.defaultStorageQuotaBytes !== undefined) fields.push("storage");
  }
  if (!settingsParsed.success) {
    if (settings.supportEmail !== undefined) fields.push("supportEmail");
    if (settings.supportPhone !== undefined) fields.push("supportPhone");
    if (settings.supportWhatsApp !== undefined) fields.push("supportWhatsApp");
    if (settings.supportUrl !== undefined) fields.push("supportUrl");
  }

  const invalid = [...new Set(fields)];
  if (invalid.length > 0 || !configParsed.success || !settingsParsed.success) {
    return { ok: false, fields: invalid };
  }
  return { ok: true, config: configParsed.data, settings: settingsParsed.data };
}
